import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";
import api from "../Utils/Axios";
function ExpenseFieldsLedger() {
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [companyLanguage, setCompanyLanguage] = useState('en');
  const printRef = useRef(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [selectedExpenseField, setSelectedExpenseField] = useState(null);
  const [expenseFields, setExpenseFields] = useState([
    { fieldName: 'Cash', fieldNameUrdu: 'نقدی', fieldType: 'number', order: 1 },
    { fieldName: 'Rent', fieldNameUrdu: 'کرایہ', fieldType: 'number', order: 2 },
    { fieldName: 'Commission', fieldNameUrdu: 'کمیشن', fieldType: 'number', order: 3 },
    { fieldName: 'Labor', fieldNameUrdu: 'مزدوری', fieldType: 'number', order: 4 },
    { fieldName: 'Miscellaneous', fieldNameUrdu: 'مشیانہ', fieldType: 'number', order: 5 },
    { fieldName: 'Market Fee', fieldNameUrdu: 'مارکیٹ فیس', fieldType: 'number', order: 6 }
  ]);
  const [expenseLedgers, setExpenseLedgers] = useState({});
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    const lang = localStorage.getItem("companyLanguage") || 'en';
    setCompanyLanguage(lang);

    // Set default date range (current month)
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    setDateRange({
      startDate: firstDay.toLocaleDateString('en-CA'),
      endDate: lastDay.toLocaleDateString('en-CA')
    });

    fetchBills();
  }, []);

  // Recalculate ledgers when date range changes
  useEffect(() => {
    if (bills.length > 0) {
      // Recalculate ledgers with new date range
      const ledgers = {};

      const fieldsToProcess = expenseFields.length > 0 ? expenseFields : [];

      // Initialize all fields first
      fieldsToProcess.forEach(field => {
        ledgers[field.fieldName] = {
          field: field,
          transactions: [],
          total: 0
        };
      });

      // Populate with filtered data
      fieldsToProcess.forEach(field => {
        const fieldKey = field.fieldName;
        const fieldKeyUrdu = field.fieldNameUrdu;
        const transactions = [];
        let total = 0;

        bills.forEach((bill) => {
          // Filter by date range
          const billDate = new Date(bill.date);
          if (dateRange.startDate && billDate < new Date(dateRange.startDate)) return;
          if (dateRange.endDate) {
            const endDate = new Date(dateRange.endDate);
            endDate.setHours(23, 59, 59, 999);
            if (billDate > endDate) return;
          }

          let expensesObj = bill.expenses || {};
          if (expensesObj instanceof Map) {
            expensesObj = Object.fromEntries(expensesObj);
          } else if (expensesObj && typeof expensesObj === 'object' && expensesObj.get) {
            const plainObj = {};
            if (expensesObj.forEach) {
              expensesObj.forEach((val, key) => {
                plainObj[key] = val;
              });
            }
            expensesObj = plainObj;
          } else if (!expensesObj || typeof expensesObj !== 'object') {
            expensesObj = {};
          }

          // Use the same matching logic as fetchBills
          let value = null;
          let matchedKey = null;
          const allExpenseKeys = Object.keys(expensesObj);

          // 1. Try exact English field name
          if (expensesObj[fieldKey] !== undefined && expensesObj[fieldKey] !== null && expensesObj[fieldKey] !== '') {
            value = expensesObj[fieldKey];
            matchedKey = fieldKey;
          }
          // 1b. For Labor, try 'Wages' first (as that's what's used in bill creation)
          else if (fieldKey === 'Labor' && expensesObj['Wages'] !== undefined && expensesObj['Wages'] !== null && expensesObj['Wages'] !== '') {
            value = expensesObj['Wages'];
            matchedKey = 'Wages';
          }
          // 1c. For Miscellaneous, try 'Mashiyana' first (as that's what's used in bill creation)
          else if (fieldKey === 'Miscellaneous' && expensesObj['Mashiyana'] !== undefined && expensesObj['Mashiyana'] !== null && expensesObj['Mashiyana'] !== '') {
            value = expensesObj['Mashiyana'];
            matchedKey = 'Mashiyana';
          }
          // 2. Try Urdu field name
          else if (fieldKeyUrdu && expensesObj[fieldKeyUrdu] !== undefined && expensesObj[fieldKeyUrdu] !== null && expensesObj[fieldKeyUrdu] !== '') {
            value = expensesObj[fieldKeyUrdu];
            matchedKey = fieldKeyUrdu;
          }
          // 3. Try matching against bill's expenseFields
          else if (bill.expenseFields && Array.isArray(bill.expenseFields)) {
            const billField = bill.expenseFields.find(f =>
              f.fieldName === fieldKey || f.fieldNameUrdu === fieldKeyUrdu ||
              (f.fieldName && f.fieldName.toLowerCase() === fieldKey.toLowerCase()) ||
              (f.fieldNameUrdu && f.fieldNameUrdu === fieldKeyUrdu)
            );
            if (billField) {
              value = expensesObj[billField.fieldName] || expensesObj[billField.fieldNameUrdu];
              if (value !== undefined && value !== null && value !== '') {
                matchedKey = billField.fieldName || billField.fieldNameUrdu;
              }
            }
          }

          // 4. Try case-insensitive match with variations
          if ((value === null || value === undefined || value === '') && expensesObj && allExpenseKeys.length > 0) {
            const fieldKeyLower = fieldKey.toLowerCase().trim();
            const fieldKeyVariations = [
              fieldKey,
              fieldKeyLower,
              fieldKeyUrdu,
              fieldKey.replace(/\s+/g, ' ').trim(),
              fieldKey.replace(/\s+/g, '').trim()
            ];

            // Add common variations for Labor and Miscellaneous
            if (fieldKey === 'Labor') {
              fieldKeyVariations.push('Wages', 'wages', 'WAGES', 'labour', 'Labour', 'LABOR', 'LABOUR', 'مزدوری');
            }
            if (fieldKey === 'Miscellaneous') {
              fieldKeyVariations.push('Mashiyana', 'mashiyana', 'MASHIYANA', 'Mashyana', 'mashyana', 'misc', 'Misc', 'MISC', 'مشیانہ', 'مشیانه');
            }

            const matched = allExpenseKeys.find(key => {
              const keyLower = key.toLowerCase().trim();
              const keyTrimmed = key.trim();

              if (fieldKeyVariations.includes(key) || fieldKeyVariations.includes(keyLower) || fieldKeyVariations.includes(keyTrimmed)) {
                return true;
              }

              if (keyLower.includes(fieldKeyLower) || fieldKeyLower.includes(keyLower)) {
                return true;
              }

              if (fieldKeyUrdu && (key === fieldKeyUrdu || key.includes(fieldKeyUrdu) || fieldKeyUrdu.includes(key))) {
                return true;
              }

              return false;
            });

            if (matched) {
              value = expensesObj[matched];
              matchedKey = matched;
            }
          }

          // 5. Last resort: Try to find by checking if any key matches the pattern
          if ((value === null || value === undefined || value === '') && expensesObj && allExpenseKeys.length > 0) {
            // For Labor, try to find keys containing "lab", "wages", "mazdoor" or "مزدوری"
            if (fieldKey === 'Labor') {
              const laborKeys = allExpenseKeys.filter(k => {
                const kLower = k.toLowerCase();
                return kLower.includes('lab') || kLower.includes('wages') || kLower.includes('mazdoor') ||
                  k.includes('مزدوری') || k.includes('مزدور') || k === 'Wages' || k === 'Labor' || k === 'Labour';
              });
              if (laborKeys.length > 0) {
                value = expensesObj[laborKeys[0]];
                matchedKey = laborKeys[0];
              }
            }

            // For Miscellaneous, try to find keys containing "misc", "mashiyana", "mashyana" or "مشیانہ"
            if (fieldKey === 'Miscellaneous') {
              const miscKeys = allExpenseKeys.filter(k => {
                const kLower = k.toLowerCase();
                return kLower.includes('misc') || kLower.includes('mashiyana') || kLower.includes('mashyana') ||
                  k.includes('مشیانہ') || k.includes('مشیانه') || k.includes('مشیان') ||
                  k === 'Mashiyana' || k === 'Miscellaneous' || k === 'Misc';
              });
              if (miscKeys.length > 0) {
                value = expensesObj[miscKeys[0]];
                matchedKey = miscKeys[0];
              }
            }
          }

          if (value !== null && value !== undefined && value !== '') {
            let amount = value;
            if (typeof amount === 'string') {
              amount = amount.replace(/Rs\.?\s*/gi, '').replace(/,/g, '').trim();
            }
            amount = Number(amount) || 0;

            if (amount >= 0) {
              if (amount > 0) {
                transactions.push({
                  billId: bill._id,
                  serialNumber: bill.serialNumber || bill._id.toString().slice(-6),
                  date: bill.date,
                  customerName: bill.customerNameUrdu || bill.customerName,
                  amount: amount
                });
              }
              total += amount;
            }
          }
        });

        ledgers[fieldKey] = {
          field: field,
          transactions: transactions.sort((a, b) => new Date(b.date) - new Date(a.date)),
          total: total
        };
      });

      setExpenseLedgers(ledgers);
    }
  }, [dateRange, bills, expenseFields]);

  const getToken = () => {
    return localStorage.getItem("companyToken");
  };

  const fetchBills = async () => {
    try {
      setIsLoading(true);
      const token = getToken();
      if (!token) {
        navigate("/company/login");
        return;
      }

      const response = await api.get('/bill/company/my-bills', {
        headers: { Authorization: `Bearer ${token}` }
      });

      const allBills = response.data.bills || [];
      setBills(allBills);

      console.log('Fetched bills:', allBills.length);
      if (allBills.length > 0) {
        console.log('Sample bill expenses:', allBills[0].expenses);
        console.log('Sample bill expenseFields:', allBills[0].expenseFields);
      }

      // Collect all unique expense fields from bills
      const allExpenseFieldsMap = new Map();
      expenseFields.forEach(field => {
        allExpenseFieldsMap.set(field.fieldName, field);
      });

      // Also collect fields from bills
      allBills.forEach(bill => {
        if (bill.expenseFields && Array.isArray(bill.expenseFields)) {
          bill.expenseFields.forEach(field => {
            if (field.fieldName && !allExpenseFieldsMap.has(field.fieldName)) {
              allExpenseFieldsMap.set(field.fieldName, field);
            }
          });
        }
      });

      // Convert to array and update state
      const allExpenseFields = Array.from(allExpenseFieldsMap.values());
      if (allExpenseFields.length > expenseFields.length) {
        setExpenseFields(allExpenseFields);
      }

      // Build expense ledgers for each field - ensure all default fields are included
      const ledgers = {};

      // Use the original expenseFields list to ensure all fields are shown
      const fieldsToProcess = expenseFields.length > 0 ? expenseFields : allExpenseFields;

      // Initialize all fields first (even with 0 values)
      fieldsToProcess.forEach(field => {
        ledgers[field.fieldName] = {
          field: field,
          transactions: [],
          total: 0
        };
      });

      // Now populate with actual data
      fieldsToProcess.forEach(field => {
        const fieldKey = field.fieldName;
        const fieldKeyUrdu = field.fieldNameUrdu;
        const transactions = [];
        let total = 0;

        // Debug for Labor and Miscellaneous
        const isDebugField = fieldKey === 'Labor' || fieldKey === 'Miscellaneous';

        allBills.forEach((bill, billIndex) => {
          // Filter by date range if provided
          const billDate = new Date(bill.date);
          if (dateRange.startDate && billDate < new Date(dateRange.startDate)) return;
          if (dateRange.endDate) {
            const endDate = new Date(dateRange.endDate);
            endDate.setHours(23, 59, 59, 999); // Include full end date
            if (billDate > endDate) return;
          }

          // Expenses should now be a plain object from backend, but handle edge cases
          let expensesObj = bill.expenses || {};

          // Convert to plain object if it's still a Map (fallback)
          if (expensesObj instanceof Map) {
            expensesObj = Object.fromEntries(expensesObj);
          } else if (expensesObj && typeof expensesObj === 'object' && expensesObj.get && typeof expensesObj.get === 'function') {
            // Map-like object
            const plainObj = {};
            if (expensesObj.forEach) {
              expensesObj.forEach((val, key) => {
                plainObj[key] = val;
              });
            } else {
              // Try common field names
              ['Vehicle', 'Cash', 'Rent', 'Commission', 'Labor', 'Miscellaneous', 'Market Fee'].forEach(f => {
                const val = expensesObj.get(f);
                if (val !== undefined) plainObj[f] = val;
              });
            }
            expensesObj = plainObj;
          } else if (!expensesObj || typeof expensesObj !== 'object') {
            expensesObj = {};
          }

          // Debug logging for Labor and Miscellaneous
          if (isDebugField && billIndex < 3) {
            console.log(`=== Debugging ${fieldKey} (Bill ${billIndex + 1}) ===`);
            console.log('Bill expenses raw:', bill.expenses);
            console.log('Bill expenses converted:', expensesObj);
            console.log('All expense keys:', Object.keys(expensesObj));
            console.log('All expense keys (lowercase):', Object.keys(expensesObj).map(k => k.toLowerCase()));
            console.log('Looking for:', fieldKey, 'or', fieldKeyUrdu);
            console.log('Direct access [fieldKey]:', expensesObj[fieldKey]);
            console.log('Direct access [fieldKeyUrdu]:', expensesObj[fieldKeyUrdu]);
            if (bill.expenseFields) {
              console.log('Bill expenseFields:', bill.expenseFields.map(f => ({ name: f.fieldName, urdu: f.fieldNameUrdu })));
            }
            // Check for partial matches
            const allKeys = Object.keys(expensesObj);
            const partialMatches = allKeys.filter(k => {
              const kLower = k.toLowerCase();
              const fieldLower = fieldKey.toLowerCase();
              return kLower.includes(fieldLower) || fieldLower.includes(kLower) || k.includes(fieldKeyUrdu) || fieldKeyUrdu.includes(k);
            });
            if (partialMatches.length > 0) {
              console.log('Partial matches found:', partialMatches);
            }
          }

          // Try multiple field name variations
          let value = null;
          let matchedKey = null;

          // Get all keys from expenses object for matching
          const allExpenseKeys = Object.keys(expensesObj);

          // 1. Try exact English field name
          if (expensesObj[fieldKey] !== undefined && expensesObj[fieldKey] !== null && expensesObj[fieldKey] !== '') {
            value = expensesObj[fieldKey];
            matchedKey = fieldKey;
          }
          // 1b. For Labor, try 'Wages' first (as that's what's used in bill creation)
          else if (fieldKey === 'Labor' && expensesObj['Wages'] !== undefined && expensesObj['Wages'] !== null && expensesObj['Wages'] !== '') {
            value = expensesObj['Wages'];
            matchedKey = 'Wages';
          }
          // 1c. For Miscellaneous, try 'Mashiyana' first (as that's what's used in bill creation)
          else if (fieldKey === 'Miscellaneous' && expensesObj['Mashiyana'] !== undefined && expensesObj['Mashiyana'] !== null && expensesObj['Mashiyana'] !== '') {
            value = expensesObj['Mashiyana'];
            matchedKey = 'Mashiyana';
          }
          // 2. Try Urdu field name
          else if (fieldKeyUrdu && expensesObj[fieldKeyUrdu] !== undefined && expensesObj[fieldKeyUrdu] !== null && expensesObj[fieldKeyUrdu] !== '') {
            value = expensesObj[fieldKeyUrdu];
            matchedKey = fieldKeyUrdu;
          }
          // 3. Try matching against bill's expenseFields
          else if (bill.expenseFields && Array.isArray(bill.expenseFields)) {
            const billField = bill.expenseFields.find(f =>
              f.fieldName === fieldKey || f.fieldNameUrdu === fieldKeyUrdu ||
              (f.fieldName && f.fieldName.toLowerCase() === fieldKey.toLowerCase()) ||
              (f.fieldNameUrdu && f.fieldNameUrdu === fieldKeyUrdu)
            );
            if (billField) {
              // Try both field names from bill
              value = expensesObj[billField.fieldName] || expensesObj[billField.fieldNameUrdu];
              if (value !== undefined && value !== null && value !== '') {
                matchedKey = billField.fieldName || billField.fieldNameUrdu;
              }
            }
          }

          // 4. Try case-insensitive match with all possible variations
          if ((value === null || value === undefined || value === '') && expensesObj && allExpenseKeys.length > 0) {
            const fieldKeyLower = fieldKey.toLowerCase().trim();
            const fieldKeyVariations = [
              fieldKey,
              fieldKeyLower,
              fieldKeyUrdu,
              fieldKey.replace(/\s+/g, ' ').trim(),
              fieldKey.replace(/\s+/g, '').trim()
            ];

            // Also add common variations for Labor and Miscellaneous
            if (fieldKey === 'Labor') {
              fieldKeyVariations.push('Wages', 'wages', 'WAGES', 'labour', 'Labour', 'LABOR', 'LABOUR', 'مزدوری');
            }
            if (fieldKey === 'Miscellaneous') {
              fieldKeyVariations.push('Mashiyana', 'mashiyana', 'MASHIYANA', 'Mashyana', 'mashyana', 'misc', 'Misc', 'MISC', 'مشیانہ', 'مشیانه');
            }

            const matched = allExpenseKeys.find(key => {
              const keyLower = key.toLowerCase().trim();
              const keyTrimmed = key.trim();

              // Check exact matches
              if (fieldKeyVariations.includes(key) || fieldKeyVariations.includes(keyLower) || fieldKeyVariations.includes(keyTrimmed)) {
                return true;
              }

              // Check if key contains field name or vice versa
              if (keyLower.includes(fieldKeyLower) || fieldKeyLower.includes(keyLower)) {
                return true;
              }

              // Check Urdu match
              if (fieldKeyUrdu && (key === fieldKeyUrdu || key.includes(fieldKeyUrdu) || fieldKeyUrdu.includes(key))) {
                return true;
              }

              return false;
            });

            if (matched) {
              value = expensesObj[matched];
              matchedKey = matched;
            }
          }

          // 5. Last resort: Try to find by checking if any key in expenses matches the field name pattern
          if ((value === null || value === undefined || value === '') && expensesObj && allExpenseKeys.length > 0) {
            // For Labor, try to find keys containing "lab", "wages", "mazdoor" or "مزدوری"
            if (fieldKey === 'Labor') {
              const laborKeys = allExpenseKeys.filter(k => {
                const kLower = k.toLowerCase();
                return kLower.includes('lab') || kLower.includes('wages') || kLower.includes('mazdoor') ||
                  k.includes('مزدوری') || k.includes('مزدور') || k === 'Wages' || k === 'Labor' || k === 'Labour';
              });
              if (laborKeys.length > 0) {
                value = expensesObj[laborKeys[0]];
                matchedKey = laborKeys[0];
              }
            }

            // For Miscellaneous, try to find keys containing "misc", "mashiyana", "mashyana" or "مشیانہ"
            if (fieldKey === 'Miscellaneous') {
              const miscKeys = allExpenseKeys.filter(k => {
                const kLower = k.toLowerCase();
                return kLower.includes('misc') || kLower.includes('mashiyana') || kLower.includes('mashyana') ||
                  k.includes('مشیانہ') || k.includes('مشیانه') || k.includes('مشیان') ||
                  k === 'Mashiyana' || k === 'Miscellaneous' || k === 'Misc';
              });
              if (miscKeys.length > 0) {
                value = expensesObj[miscKeys[0]];
                matchedKey = miscKeys[0];
              }
            }
          }

          // Debug: Log if value found
          if (isDebugField && billIndex < 3) {
            console.log(`Bill ${billIndex + 1} - ${fieldKey}:`, {
              value: value,
              matchedKey: matchedKey,
              amount: value ? (Number(value) || 0) : 0
            });
          }

          // Process value if found
          if (value !== null && value !== undefined && value !== '') {
            // Handle string values that might contain "Rs." or other text
            let amount = value;
            if (typeof amount === 'string') {
              amount = amount.replace(/Rs\.?\s*/gi, '').replace(/,/g, '').trim();
            }
            amount = Number(amount) || 0;

            // Include zero values too, but only add to transactions if > 0
            if (amount >= 0) {
              if (amount > 0) {
                transactions.push({
                  billId: bill._id,
                  serialNumber: bill.serialNumber || bill._id.toString().slice(-6),
                  date: bill.date,
                  customerName: bill.customerNameUrdu || bill.customerName,
                  amount: amount
                });
              }
              total += amount;
            }
          }
        });

        ledgers[fieldKey] = {
          field: field,
          transactions: transactions.sort((a, b) => new Date(b.date) - new Date(a.date)),
          total: total
        };

        console.log(`Ledger for ${fieldKey}:`, {
          transactions: transactions.length,
          total: total
        });
      });

      setExpenseLedgers(ledgers);

      // Select first field by default
      const fieldsToUse = expenseFields.length > 0 ? expenseFields : allExpenseFields;
      if (fieldsToUse.length > 0 && !selectedExpenseField) {
        setSelectedExpenseField(fieldsToUse[0].fieldName);
      }

      // Debug: Log all ledgers
      console.log('All expense ledgers:', Object.keys(ledgers).map(key => ({
        field: key,
        total: ledgers[key].total,
        transactions: ledgers[key].transactions.length
      })));
    } catch (error) {
      console.error('Fetch bills error:', error);
      toast.error(companyLanguage === 'ur' ? 'بلز نہیں مل سکیں' : 'Failed to load bills');
    } finally {
      setIsLoading(false);
    }
  };

  const isUrdu = companyLanguage === 'ur';
  const selectedLedger = selectedExpenseField ? expenseLedgers[selectedExpenseField] : null;
  const totalAllExpenses = Object.values(expenseLedgers).reduce((sum, ledger) => sum + (ledger?.total || 0), 0);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadExcel = () => {
    if (!selectedLedger) {
      toast.error(isUrdu ? 'کوئی اخراجات فیلڈ منتخب نہیں' : 'No expense field selected');
      return;
    }

    try {
      const header = [
        isUrdu ? 'تاریخ' : 'Date',
        isUrdu ? 'بل نمبر' : 'Bill Number',
        isUrdu ? 'گاہک' : 'Customer',
        isUrdu ? 'رقم' : 'Amount',
      ];

      const rows = selectedLedger.transactions.map((t) => ([
        new Date(t.date).toLocaleDateString(),
        t.serialNumber || '-',
        t.customerName || '-',
        Number(t.amount || 0),
      ]));

      // Total row
      rows.push(['', isUrdu ? 'کل' : 'Total', '', Number(selectedLedger.total || 0)]);

      const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
      const wb = XLSX.utils.book_new();
      const sheetName = (isUrdu ? (selectedLedger.field?.fieldNameUrdu || 'اخراجات') : (selectedLedger.field?.fieldName || 'Expenses')).slice(0, 31);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      const fileName = `ExpenseFieldsLedger_${selectedExpenseField || 'field'}_${new Date().toLocaleDateString('en-CA')}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success(isUrdu ? 'Excel فائل ڈاؤن لوڈ ہو گئی' : 'Excel downloaded');
    } catch (e) {
      console.error('Excel export error:', e);
      toast.error(isUrdu ? 'Excel بنانے میں ناکام' : 'Failed to export Excel');
    }
  };

  const handleDownloadPDF = async () => {
    if (!printRef.current || !selectedLedger) {
      toast.error(isUrdu ? 'مواد نہیں ملا' : 'Content not found');
      return;
    }

    try {
      setIsGeneratingPDF(true);
      toast.info(isUrdu ? 'PDF بنایا جا رہا ہے...' : 'Generating PDF...');

      const element = printRef.current;

      // Temporarily hide interactive controls in the printed panel
      const hiddenEls = element.querySelectorAll('[data-export-hide="true"]');
      hiddenEls.forEach((el) => (el.style.display = 'none'));

      const style = document.createElement('style');
      style.id = 'expense-ledger-pdf-style';
      style.textContent = `
        .bg-gradient-to-r { background: white !important; }
        .shadow-lg { box-shadow: none !important; }
        .rounded-lg, .rounded-xl { border-radius: 0 !important; }
        table { width: 100% !important; }
      `;
      document.head.appendChild(style);

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
      });

      hiddenEls.forEach((el) => (el.style.display = ''));
      const tempStyle = document.getElementById('expense-ledger-pdf-style');
      if (tempStyle) document.head.removeChild(tempStyle);

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const fieldName = isUrdu ? (selectedLedger.field?.fieldNameUrdu || 'اخراجات') : (selectedLedger.field?.fieldName || 'Expenses');
      const fileName = `ExpenseFieldsLedger_${fieldName}_${new Date().toLocaleDateString('en-CA')}.pdf`;
      pdf.save(fileName);
      toast.success(isUrdu ? 'PDF ڈاؤن لوڈ ہو گیا!' : 'PDF downloaded');
    } catch (e) {
      console.error('PDF export error:', e);
      toast.error(isUrdu ? 'PDF بنانے میں ناکام' : 'Failed to export PDF');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <>
      <Helmet><title>{isUrdu ? 'اخراجات کھاتے' : 'Expense Fields Ledger'} - Company Dashboard</title></Helmet>

      <div className={`min-h-screen bg-brand-background pt-[6px] pb-6 px-4 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}} dir={isUrdu ? 'rtl' : 'ltr'}>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-brand-header to-emerald-800 rounded-xl shadow-lg p-3 mb-3 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h1 className={`text-xl font-bold mb-1 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {isUrdu ? 'اخراجات کے کھاتے' : 'Expense Fields Ledger'}
                </h1>
                <p className="text-white/80 text-sm">
                  {isUrdu ? 'تمام اخراجات کے الگ الگ کھاتے' : 'Separate ledgers for all expense fields'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-white/80">{isUrdu ? 'کل اخراجات:' : 'Total Expenses:'}</p>
                <p className="text-xl font-bold">{totalAllExpenses.toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Date Range Filter */}
          <div className="mb-2 p-1 bg-gradient-to-r from-green-50 to-lime-50 rounded-xl border border-green-200 animate-fade-in-down shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-1.5 items-end">
              <div className="min-w-0">
                <label className={`block text-[10px] font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {isUrdu ? 'شروع کی تاریخ:' : 'Start Date:'}
                </label>
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => {
                    setDateRange({ ...dateRange, startDate: e.target.value });
                    setTimeout(() => fetchBills(), 100);
                  }}
                  className="w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs leading-tight"
                />
              </div>
              <div className="min-w-0">
                <label className={`block text-[10px] font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {isUrdu ? 'اختتام کی تاریخ:' : 'End Date:'}
                </label>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => {
                    setDateRange({ ...dateRange, endDate: e.target.value });
                    setTimeout(() => fetchBills(), 100);
                  }}
                  className="w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs leading-tight"
                />
              </div>
              <div className="flex items-end gap-2 md:justify-self-end">
                <button
                  onClick={() => {
                    setDateRange({ startDate: '', endDate: '' });
                    setTimeout(() => fetchBills(), 100);
                  }}
                  className="px-2 py-1 bg-gray-500 hover:bg-gray-600 text-white text-xs rounded-lg transition-all"
                >
                  {isUrdu ? 'کلیر' : 'Clear'}
                </button>
                <button
                  onClick={fetchBills}
                  className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded-lg transition-all"
                >
                  {isUrdu ? 'تازہ کریں' : 'Refresh'}
                </button>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4 items-start">
            {/* Expense Fields List */}
            <div className="md:col-span-1 bg-white rounded-lg shadow-lg p-3 border border-brand-card flex flex-col max-h-[calc(100vh-320px)]">
              <div className={`flex items-center justify-between mb-4 ${isUrdu ? 'flex-row-reverse' : ''}`}>
                <h3 className={`text-lg font-semibold text-brand-header ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {isUrdu ? 'اخراجات' : 'Expense Fields'}
                </h3>
              </div>

              {/* Expense Fields List */}
              <div className="flex-1 overflow-y-auto">
                {isLoading ? (
                  <p className={`text-gray-500 text-sm text-center py-4 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {isUrdu ? 'لوڈ ہو رہا ہے...' : 'Loading...'}
                  </p>
                ) : (
                  <ul className="divide-y divide-gray-200">
                    {expenseFields.map((field) => {
                      const ledger = expenseLedgers[field.fieldName];
                      const total = ledger?.total || 0;
                      const count = ledger?.transactions?.length || 0;

                      return (
                        <li
                          key={field.fieldName}
                          onClick={() => setSelectedExpenseField(field.fieldName)}
                          className={`py-3 px-3 cursor-pointer rounded-lg transition ${selectedExpenseField === field.fieldName
                              ? 'bg-brand-header text-white shadow-md'
                              : 'hover:bg-brand-background'
                            }`}
                        >
                          <div className="flex justify-between items-center">
                            <span className={`font-semibold text-base ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                              {isUrdu ? field.fieldNameUrdu : field.fieldName}
                            </span>
                          </div>
                          <div className={`flex justify-between items-center mt-2 ${selectedExpenseField === field.fieldName ? 'text-white/90' : 'text-gray-600'}`}>
                            <span className="text-xs">
                              {isUrdu ? `${count} اندراجات` : `${count} entries`}
                            </span>
                            <span className={`font-bold ${selectedExpenseField === field.fieldName ? 'text-white' : 'text-brand-header'}`}>
                              {total.toLocaleString()}
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>

            {/* Transactions */}
            <div ref={printRef} className="md:col-span-2 bg-white rounded-lg shadow-lg p-4 border border-brand-card max-h-[calc(100vh-320px)] flex flex-col">
              {selectedLedger ? (
                <>
                  {/* Print/PDF header (only) */}
                  <div className="hidden print:block border-b border-gray-300 pb-2 mb-3">
                    <div className="flex justify-between items-start gap-3">
                      <div>
                        <h2 className={`text-lg font-bold ${isUrdu ? 'font-urdu' : ''}`}>
                          {isUrdu ? 'اخراجات کے کھاتے' : 'Expense Fields Ledger'}
                        </h2>
                        <p className="text-xs text-gray-600">
                          {isUrdu ? (selectedLedger.field?.fieldNameUrdu || '') : (selectedLedger.field?.fieldName || '')}
                        </p>
                      </div>
                      <div className="text-xs text-gray-600 text-right">
                        <div>
                          {isUrdu ? 'تاریخ:' : 'Date:'} {new Date().toLocaleDateString()}
                        </div>
                        <div>
                          {isUrdu ? 'رینج:' : 'Range:'}{' '}
                          {(dateRange.startDate || '-') + ' → ' + (dateRange.endDate || '-')}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={`flex justify-between items-center mb-6 ${isUrdu ? 'flex-row-reverse' : ''}`}>
                    <div>
                      <h3 className={`text-2xl font-bold text-brand-header mb-2 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                        {isUrdu ? selectedLedger.field.fieldNameUrdu : selectedLedger.field.fieldName}
                      </h3>
                      <p className={`text-sm text-gray-600 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                        {isUrdu ? 'کل رقم:' : 'Total Amount:'}{' '}
                        <span className="font-bold text-brand-header text-lg">
                          {selectedLedger.total.toLocaleString()}
                        </span>
                      </p>
                    </div>
                    <div className={`flex items-center gap-2 ${isUrdu ? 'flex-row-reverse' : ''}`} data-export-hide="true">
                      <button
                        type="button"
                        onClick={handleDownloadExcel}
                        className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded-lg transition-all"
                      >
                        {isUrdu ? 'Excel' : 'Excel'}
                      </button>
                      <button
                        type="button"
                        onClick={handlePrint}
                        className="px-2 py-1 bg-gray-700 hover:bg-gray-800 text-white text-xs rounded-lg transition-all"
                      >
                        {isUrdu ? 'پرنٹ' : 'Print'}
                      </button>
                      <button
                        type="button"
                        disabled={isGeneratingPDF}
                        onClick={handleDownloadPDF}
                        className={`px-2 py-1 text-white text-xs rounded-lg transition-all ${isGeneratingPDF ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                          }`}
                      >
                        {isGeneratingPDF ? (isUrdu ? 'PDF...' : 'PDF...') : 'PDF'}
                      </button>
                    </div>
                    <div className="text-right" data-export-hide="true">
                      <p className="text-sm text-gray-600">
                        {isUrdu ? 'کل اندراجات:' : 'Total Entries:'}
                      </p>
                      <p className="text-xl font-bold text-brand-header">
                        {selectedLedger.transactions.length}
                      </p>
                    </div>
                  </div>

                  <div className="overflow-auto flex-1">
                    {selectedLedger.transactions.length === 0 ? (
                      <div className="text-center py-12">
                        <p className={`text-gray-500 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                          {isUrdu ? 'ابھی کوئی اندراج نہیں' : 'No entries yet'}
                        </p>
                      </div>
                    ) : (
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-brand-background">
                          <tr>
                            <th className={`px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                              {isUrdu ? 'تاریخ' : 'Date'}
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                              {isUrdu ? 'بل نمبر' : 'Bill Number'}
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                              {isUrdu ? 'گاہک' : 'Customer'}
                            </th>
                            <th className={`px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                              {isUrdu ? 'رقم' : 'Amount'}
                            </th>
                            <th className={`px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                              {isUrdu ? 'عمل' : 'Action'}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {selectedLedger.transactions.map((transaction, idx) => (
                            <tr key={idx} className="hover:bg-brand-background/50 transition">
                              <td className={`px-4 py-3 whitespace-nowrap text-gray-700 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                                {new Date(transaction.date).toLocaleDateString()}
                              </td>
                              <td className={`px-4 py-3 whitespace-nowrap text-gray-700 font-semibold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                                {isUrdu ? 'بل #' : 'Bill #'}{transaction.serialNumber}
                              </td>
                              <td className={`px-4 py-3 whitespace-nowrap text-gray-700 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                                {transaction.customerName || '-'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-right font-semibold text-brand-header">
                                {transaction.amount.toLocaleString()}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <button
                                  onClick={() => window.open(`/bill/view/${transaction.billId}`, '_blank')}
                                  className="bg-brand-header hover:bg-emerald-800 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-all transform hover:scale-105"
                                >
                                  {isUrdu ? 'دیکھیں' : 'View'}
                                </button>
                              </td>
                            </tr>
                          ))}

                          {/* Total Row */}
                          <tr className="bg-brand-background font-bold">
                            <td className={`px-4 py-3 text-gray-700 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}} colSpan={3}>
                              {isUrdu ? 'کل' : 'Total'}
                            </td>
                            <td className="px-4 py-3 text-right font-bold text-brand-header text-lg">
                              {selectedLedger.total.toLocaleString()}
                            </td>
                            <td className="px-4 py-3"></td>
                          </tr>
                        </tbody>
                      </table>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <p className={`text-gray-500 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {isUrdu ? 'کوئی اخراجات کا فیلڈ منتخب نہیں' : 'No expense field selected'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ExpenseFieldsLedger;

